<template>
  <div class="dashboard">
    <p class="message">📊 Progreso</p>

    <div class="cards">
      <div class="card">
        <div class="card-title">Hoy</div>
        <div class="kpis">
          <div class="kpi"><span>Sesiones</span><b>{{ today.sessions }}</b></div>
          <div class="kpi"><span>Completadas</span><b>{{ today.completed }}</b></div>
          <div class="kpi"><span>Tiempo</span><b>{{ fmtMs(today.totalMs) }}</b></div>
        </div>
      </div>

      <div class="card">
        <div class="card-title">Últimos 7 días</div>
        <div class="kpis">
          <div class="kpi"><span>Sesiones</span><b>{{ last7.sessions }}</b></div>
          <div class="kpi"><span>Completadas</span><b>{{ last7.completed }}</b></div>
          <div class="kpi"><span>Tiempo</span><b>{{ fmtMs(last7.totalMs) }}</b></div>
        </div>
      </div>

      <div class="card">
        <div class="card-title">Últimos 30 días</div>
        <div class="kpis">
          <div class="kpi"><span>Sesiones</span><b>{{ last30.sessions }}</b></div>
          <div class="kpi"><span>Completadas</span><b>{{ last30.completed }}</b></div>
          <div class="kpi"><span>Tiempo</span><b>{{ fmtMs(last30.totalMs) }}</b></div>
        </div>
      </div>
    </div>

    <div class="sections">
      <div class="section">
        <div class="section-head">
          <h3 class="section-title">Tendencia diaria (completadas)</h3>
          <div class="legend">
            <span class="dot dot-completed"></span>
            <span>Completadas por día</span>
          </div>
        </div>

        <div ref="trendWrap" class="trend-wrap">
          <canvas
            ref="trendCanvas"
            class="trend-canvas"
            aria-label="Gráfico de completadas por día"
            role="img"
          ></canvas>
        </div>

        <div v-if="loading" class="skeleton-chart"></div>
      </div>

      <div class="section">
        <div class="section-head">
          <h3 class="section-title">Categorías (30d)</h3>
          <small class="muted">Agrupado por tipo de nivel</small>
        </div>

        <div class="cats-row" v-if="categoriesList.length">
          <button
            v-for="cat in categoriesList"
            :key="cat.key"
            type="button"
            class="cat-chip"
            :class="{ active: activeCat === cat.key }"
            @click="toggleCat(cat.key)"
            :aria-pressed="activeCat === cat.key"
            :title="cat.title"
          >
            <span class="cat-emoji" aria-hidden="true">{{ cat.emoji }}</span>
            <span class="cat-title">{{ cat.title }}</span>
            <span class="cat-metrics">
              <b>{{ cat.totals.completed }}</b> comp • {{ cat.totals.sessions }} ses
            </span>
          </button>
        </div>

        <div v-if="activeCategory" class="category-block">
          <div class="category-head">
            <div class="category-name">
              <span class="cat-emoji big" aria-hidden="true">{{ activeCategory.emoji }}</span>
              <h4>{{ activeCategory.title }}</h4>
            </div>
            <div class="category-totals">
              <span>Sesiones: <b>{{ activeCategory.totals.sessions }}</b></span>
              <span>Completadas: <b>{{ activeCategory.totals.completed }}</b></span>
              <span>Tiempo: <b>{{ fmtMs(activeCategory.totals.totalMs) }}</b></span>
            </div>
          </div>

          <ul class="levels">
            <li
              v-for="lvl in activeCategory.levels"
              :key="lvl.id"
              class="level-row"
            >
              <div class="level-title">
                {{ lvl.title || lvl.id }}
                <small class="muted" v-if="lvl.subtitle"> · {{ lvl.subtitle }}</small>
              </div>
              <div class="level-kpis">
                <span>Sesiones: <b>{{ lvl.sessions }}</b></span>
                <span>Completadas: <b>{{ lvl.completed }}</b></span>
                <span>Tiempo: <b>{{ fmtMs(lvl.totalMs) }}</b></span>
              </div>
            </li>
          </ul>
        </div>

        <div v-else class="muted">No hay categorías para mostrar.</div>
      </div>
    </div>

    <div v-if="error" class="error">
      {{ error }}
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";

export default {
  name: "DashboardStats",
  props: {
    userId: { type: String, default: "" },
    days: { type: Number, default: 35 }
  },
  data() {
    return {
      dpr: typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1,
      resizeHandler: null,
      ro: null,
      rafId: 0,
      hoverIndex: -1,
      points: [],
      activeCat: ""
    };
  },
  computed: {
    ...mapState("auth", ["authReady"]),
    ...mapGetters("auth", ["loggedInUser", "userData"]),
    ...mapState("dashboardStats", ["loading", "error", "dailySeries"]),
    ...mapGetters("dashboardStats", [
      "totalsToday",
      "totals7d",
      "totals30d",
      "topLevels30d",
    ]),

    effectiveUserId() {
      return this.userId || this.loggedInUser?.uid || "";
    },

    effectiveKidId() {
      return this.userData?.activeKidId || this.userData?.kidId || "";
    },

    fetchKey() {
      return `${this.authReady ? 1 : 0}|${this.effectiveUserId}|${this.effectiveKidId}|${this.days}`;
    },

    today() {
      return this.totalsToday || { sessions: 0, completed: 0, totalMs: 0 };
    },
    last7() {
      return this.totals7d || { sessions: 0, completed: 0, totalMs: 0 };
    },
    last30() {
      return this.totals30d || { sessions: 0, completed: 0, totalMs: 0 };
    },
    allLevels() {
      return Array.isArray(this.topLevels30d) ? this.topLevels30d : [];
    },
    locale() {
      return "es-ES";
    },

    categoriesMap() {
      const map = new Map();

      const ensure = (key, title, emoji) => {
        if (!map.has(key)) {
          map.set(key, {
            key,
            title,
            emoji,
            totals: { sessions: 0, completed: 0, totalMs: 0 },
            levels: []
          });
        }
        return map.get(key);
      };

      this.allLevels.forEach((lvl) => {
        const cat = this.inferCategory(lvl);
        const bucket = ensure(cat.key, cat.title, cat.emoji);

        const sessions = Number(lvl.sessions || 0);
        const completed = Number(lvl.completed || 0);
        const totalMs = Number(lvl.totalMs || 0);

        bucket.totals.sessions += sessions;
        bucket.totals.completed += completed;
        bucket.totals.totalMs += totalMs;

        bucket.levels.push({
          id: lvl.id,
          title: lvl.title || lvl.id,
          subtitle: lvl.subtitle || "",
          sessions,
          completed,
          totalMs
        });
      });

      for (const bucket of map.values()) {
        bucket.levels.sort((a, b) => b.completed - a.completed || b.sessions - a.sessions);
      }

      return map;
    },

    categoriesList() {
      const arr = Array.from(this.categoriesMap.values());
      arr.sort((a, b) => b.totals.completed - a.totals.completed || b.totals.sessions - a.totals.sessions);
      return arr;
    },

    activeCategory() {
      if (!this.categoriesList.length) return null;
      const current = this.activeCat || this.categoriesList[0]?.key;
      return this.categoriesList.find((c) => c.key === current) || this.categoriesList[0];
    }
  },

  mounted() {
    this.resizeHandler = () => this.scheduleDraw();
    window.addEventListener("resize", this.resizeHandler);

    const wrap = this.$refs.trendWrap;
    if (window.ResizeObserver && wrap) {
      this.ro = new ResizeObserver(() => {
        this.scheduleDraw();
      });
      this.ro.observe(wrap);
    }

    const canvas = this.$refs.trendCanvas;
    if (canvas) {
      canvas.addEventListener("mousemove", this.onCanvasMove);
      canvas.addEventListener("mouseleave", this.onCanvasLeave);
      canvas.addEventListener("touchstart", this.onTouchMove, { passive: true });
      canvas.addEventListener("touchmove", this.onTouchMove, { passive: true });
      canvas.addEventListener("touchend", this.onCanvasLeave, { passive: true });
      canvas.addEventListener("touchcancel", this.onCanvasLeave, { passive: true });
    }

    this.fetchStats();
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.resizeHandler);

    if (this.ro) {
      this.ro.disconnect();
      this.ro = null;
    }

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }

    const canvas = this.$refs.trendCanvas;
    if (canvas) {
      canvas.removeEventListener("mousemove", this.onCanvasMove);
      canvas.removeEventListener("mouseleave", this.onCanvasLeave);
      canvas.removeEventListener("touchstart", this.onTouchMove);
      canvas.removeEventListener("touchmove", this.onTouchMove);
      canvas.removeEventListener("touchend", this.onCanvasLeave);
      canvas.removeEventListener("touchcancel", this.onCanvasLeave);
    }
  },

  watch: {
    fetchKey() {
      this.fetchStats();
    },

    dailySeries() {
      this.$nextTick(() => this.scheduleDraw());
    },

    categoriesList: {
      handler(list) {
        if (!list?.length) {
          this.activeCat = "";
          return;
        }
        if (!this.activeCat || !list.find((c) => c.key === this.activeCat)) {
          this.activeCat = list[0].key;
        }
      },
      immediate: true
    }
  },

  methods: {
    async fetchStats() {
      if (!this.authReady) return;

      if (!this.effectiveUserId) {
        this.$store.commit("dashboardStats/setError", "No hay ID de usuario disponible.");
        this.$store.commit("dashboardStats/setDaily", []);
        this.$store.commit("dashboardStats/setDailySeries", []);
        this.$store.commit("dashboardStats/setLevelsAgg", {});
        return;
      }

      if (!this.effectiveKidId) {
        this.$store.commit("dashboardStats/setError", "No hay kidId disponible.");
        this.$store.commit("dashboardStats/setDaily", []);
        this.$store.commit("dashboardStats/setDailySeries", []);
        this.$store.commit("dashboardStats/setLevelsAgg", {});
        return;
      }

      try {
        await this.$store.dispatch("dashboardStats/fetch", {
          userId: this.effectiveUserId,
          kidId: this.effectiveKidId,
          days: this.days
        });

        if (this.categoriesList.length && !this.activeCat) {
          this.activeCat = this.categoriesList[0].key;
        }

        this.$nextTick(() => this.scheduleDraw());
      } catch {
        this.$nextTick(() => this.scheduleDraw());
      }
    },

    scheduleDraw() {
      if (this.rafId) return;

      this.rafId = requestAnimationFrame(() => {
        this.rafId = 0;
        this.drawTrend();
      });
    },

    toggleCat(key) {
      this.activeCat = key;
    },

    fmtMs(ms = 0) {
      const s = Math.floor(ms / 1000);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const ss = s % 60;
      const pad = (n) => String(n).padStart(2, "0");
      return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${m}:${pad(ss)}`;
    },

    parseDateSafe(d) {
      const raw = d?.date ?? d?.day ?? d?.ts ?? d?.time ?? d;
      if (raw == null) return null;

      let dt;

      if (typeof raw === "number") {
        dt = new Date(raw);
      } else if (typeof raw === "string") {
        const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (m) {
          const y = parseInt(m[1], 10);
          const mo = parseInt(m[2], 10) - 1;
          const da = parseInt(m[3], 10);
          dt = new Date(y, mo, da);
        } else {
          dt = new Date(raw);
        }
      } else if (raw instanceof Date) {
        dt = new Date(raw.getTime());
      } else {
        return null;
      }

      if (Number.isNaN(dt.getTime())) return null;

      return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    },

    fmtDayLabel(d) {
      const dt = this.parseDateSafe(d);
      if (!dt) return "";

      const weekday = dt.toLocaleDateString(this.locale, { weekday: "short" });
      const day = dt.toLocaleDateString(this.locale, { day: "2-digit", month: "2-digit" });
      return `${weekday} ${day}`;
    },

    inferCategory(lvl) {
      const raw = (lvl?.category || lvl?.cat || lvl?.group || "").toString().toLowerCase();
      const title = (lvl?.title || "").toString().toLowerCase();
      const id = (lvl?.id || "").toString().toLowerCase();
      const tags = (lvl?.tags || []).map((t) => String(t).toLowerCase());

      const has = (...kw) => {
        const pool = [raw, title, id, ...tags];
        return kw.some((k) => pool.some((p) => p.includes(k)));
      };

      if (has("letra", "alfabeto", "abc", "letters")) {
        return { key: "letters", title: "Letras", emoji: "🔤" };
      }
      if (has("número", "numero", "números", "numeros", "123", "count", "digits")) {
        return { key: "numbers", title: "Números", emoji: "🔢" };
      }
      if (has("profesión", "profesion", "profesiones", "professions", "oficio")) {
        return { key: "professions", title: "Profesiones", emoji: "👩‍🏫" };
      }
      if (has("animal", "animales", "zoo")) {
        return { key: "animals", title: "Animales", emoji: "🐾" };
      }
      if (has("color", "colores", "colors")) {
        return { key: "colors", title: "Colores", emoji: "🎨" };
      }
      if (has("forma", "formas", "shapes")) {
        return { key: "shapes", title: "Formas", emoji: "🟦" };
      }
      if (has("transporte", "vehículo", "vehiculo", "coches", "cars", "trains")) {
        return { key: "transport", title: "Transporte", emoji: "🚗" };
      }
      if (has("comida", "alimentos", "frutas", "food")) {
        return { key: "food", title: "Comida", emoji: "🍎" };
      }

      return { key: "others", title: "Otros", emoji: "✨" };
    },

    onCanvasMove(e) {
      const canvas = this.$refs.trendCanvas;
      if (!canvas || !this.points.length) return;

      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) * this.dpr;
      const y = (e.clientY - rect.top) * this.dpr;

      let best = { i: -1, dist2: Infinity };
      const hitR = 14 * this.dpr;
      const hitR2 = hitR * hitR;

      this.points.forEach((p, i) => {
        const dx = p.x - x;
        const dy = p.y - y;
        const d2 = dx * dx + dy * dy;
        if (d2 < best.dist2) best = { i, dist2: d2 };
      });

      this.hoverIndex = best.dist2 <= hitR2 ? best.i : -1;
      this.drawTrend();
    },

    onTouchMove(e) {
      const t = e.touches?.[0];
      if (!t) return;

      const canvas = this.$refs.trendCanvas;
      if (!canvas || !this.points.length) return;

      const rect = canvas.getBoundingClientRect();
      const x = (t.clientX - rect.left) * this.dpr;

      let best = { i: -1, dist: Infinity };
      this.points.forEach((p, i) => {
        const d = Math.abs(p.x - x);
        if (d < best.dist) best = { i, dist: d };
      });

      this.hoverIndex = best.i;
      this.drawTrend();
    },

    onCanvasLeave() {
      this.hoverIndex = -1;
      this.drawTrend();
    },

    drawTrend() {
      const canvas = this.$refs.trendCanvas;
      if (!canvas) return;

      const wrap = this.$refs.trendWrap || canvas.parentElement;
      const wrapW = Math.max(280, Math.min(980, wrap?.clientWidth || 640));
      const baseH = wrapW < 420 ? 180 : wrapW < 640 ? 200 : 220;
      const rectH = Math.round(Math.min(320, Math.max(160, baseH)));

      const targetW = Math.round(wrapW * this.dpr);
      const targetH = Math.round(rectH * this.dpr);

      if (canvas.width !== targetW) canvas.width = targetW;
      if (canvas.height !== targetH) canvas.height = targetH;
      if (canvas.style.width !== `${wrapW}px`) canvas.style.width = `${wrapW}px`;
      if (canvas.style.height !== `${rectH}px`) canvas.style.height = `${rectH}px`;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (!Array.isArray(this.dailySeries) || this.dailySeries.length === 0) {
        this.points = [];
        return;
      }

      const W = canvas.width;
      const H = canvas.height;

      const padL = 52 * this.dpr;
      const padR = 16 * this.dpr;
      const padT = 14 * this.dpr;
      const padB = 38 * this.dpr;

      const values = this.dailySeries.map((d) => {
        const v = Number(d?.completed ?? d?.sessions ?? 0);
        return Number.isNaN(v) ? 0 : v;
      });

      const maxV = Math.max(1, ...values);

      const niceStep = (m) => {
        if (m <= 4) return 1;
        if (m <= 10) return 2;
        if (m <= 20) return 5;
        if (m <= 50) return 10;
        if (m <= 100) return 20;
        if (m <= 250) return 50;
        return Math.ceil(m / 6);
      };

      const stepYVal = niceStep(maxV);
      const maxTickVal = Math.ceil(maxV / stepYVal) * stepYVal;
      const yScale = (H - padT - padB) / (maxTickVal || 1);

      const n = this.dailySeries.length;
      const stepX = n > 1 ? (W - padL - padR) / (n - 1) : 0;

      const fontBase = 12 * this.dpr;
      ctx.font = `${fontBase}px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial`;
      ctx.textBaseline = "middle";
      ctx.fillStyle = "rgba(0,0,0,0.6)";

      ctx.strokeStyle = "rgba(0,0,0,0.10)";
      ctx.lineWidth = 1 * this.dpr;

      for (let v = 0; v <= maxTickVal; v += stepYVal) {
        const y = H - padB - v * yScale;

        ctx.beginPath();
        ctx.moveTo(padL, y);
        ctx.lineTo(W - padR, y);
        ctx.stroke();

        ctx.fillStyle = "rgba(0,0,0,0.55)";
        ctx.textAlign = "right";
        ctx.fillText(String(v), padL - 8 * this.dpr, y);
      }

      const labelEvery = Math.max(1, Math.ceil(n / (wrapW < 420 ? 4 : wrapW < 720 ? 6 : 8)));
      ctx.fillStyle = "rgba(0,0,0,0.6)";
      ctx.textAlign = "center";

      this.dailySeries.forEach((d, i) => {
        if (i % labelEvery !== 0 && i !== n - 1) return;
        const x = padL + i * stepX;
        const lbl = this.fmtDayLabel(d);
        const y = H - padB + 16 * this.dpr;
        if (lbl) ctx.fillText(lbl, x, y);
      });

      const lineColor = "#38D5F4";
      const fillColor = "rgba(56, 213, 244, 0.18)";

      ctx.lineWidth = 2.5 * this.dpr;
      ctx.strokeStyle = lineColor;
      ctx.fillStyle = fillColor;

      this.points = [];
      ctx.beginPath();

      this.dailySeries.forEach((d, i) => {
        const val = values[i];
        const x = padL + i * stepX;
        const y = H - padB - val * yScale;
        this.points.push({ x, y, val, label: this.fmtDayLabel(d) });

        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });

      ctx.stroke();

      ctx.lineTo(padL + (n - 1) * stepX, H - padB);
      ctx.lineTo(padL, H - padB);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = "#2d2949";
      const rPoint = 3.5 * this.dpr;

      this.points.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, rPoint, 0, Math.PI * 2);
        ctx.fill();
      });

      if (this.hoverIndex >= 0 && this.points[this.hoverIndex]) {
        const p = this.points[this.hoverIndex];

        ctx.strokeStyle = "rgba(0,0,0,0.12)";
        ctx.lineWidth = 1 * this.dpr;
        ctx.beginPath();
        ctx.moveTo(p.x, padT);
        ctx.lineTo(p.x, H - padB);
        ctx.stroke();

        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(p.x, p.y, 5 * this.dpr, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = lineColor;
        ctx.lineWidth = 2 * this.dpr;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 5 * this.dpr, 0, Math.PI * 2);
        ctx.stroke();

        const pad = 8 * this.dpr;
        const line1 = p.label || "";
        const line2 = `Completadas: ${p.val}`;

        ctx.font = `${fontBase + 1}px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial`;

        const w = Math.max(ctx.measureText(line1).width, ctx.measureText(line2).width) + pad * 2;
        const h = (fontBase + 6 * this.dpr) * 2;

        let tx = p.x + 10 * this.dpr;
        let ty = p.y - h - 10 * this.dpr;

        if (tx + w > W - padR) tx = p.x - w - 10 * this.dpr;
        if (ty < padT) ty = p.y + 10 * this.dpr;

        ctx.fillStyle = "rgba(45, 41, 73, 0.95)";
        const r = 8 * this.dpr;

        ctx.beginPath();
        ctx.moveTo(tx + r, ty);
        ctx.arcTo(tx + w, ty, tx + w, ty + h, r);
        ctx.arcTo(tx + w, ty + h, tx, ty + h, r);
        ctx.arcTo(tx, ty + h, tx, ty, r);
        ctx.arcTo(tx, ty, tx + w, ty, r);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = "#ffffff";
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(line1, tx + pad, ty + pad - 2 * this.dpr);
        ctx.fillText(line2, tx + pad, ty + pad + (fontBase + 2 * this.dpr));
      }
    }
  }
};
</script>

<style scoped>
.dashboard {
  --bg: #f5f7fb;
  --ink: #2d2949;
  --muted: #6b6b6b;
  --card-bg: #ffffff;
  --soft: #f8f7ff;
  --inset: #e9e7f5;
  --accent: #38D5F4;

  position: relative;
  width: 100%;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 20px 0 40px;
  overflow: hidden;
  background:
    radial-gradient(1200px 400px at 80% -10%, rgba(56,213,244,0.08), transparent 60%),
    radial-gradient(1000px 380px at -10% 0%, rgba(128,106,255,0.06), transparent 60%),
    var(--bg);
}

.message {
  font-size: clamp(1.6rem, 2.2vw + 1rem, 2.2rem);
  font-weight: 800;
  text-align: center;
  margin: 8px 0 8px;
  z-index: 12;
  color: var(--ink);
  letter-spacing: -0.02em;
}

.cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  width: min(980px, 94vw);
  margin-top: 8px;
  z-index: 5;
}

@media (max-width: 620px) {
  .cards {
    grid-template-columns: 1fr;
  }
}

.card {
  background: var(--card-bg);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(43,31,86,0.08), 0 2px 6px rgba(0,0,0,0.04);
  padding: 14px 16px;
  transition: transform 140ms ease, box-shadow 140ms ease;
  border: 1px solid rgba(45,41,73,0.06);
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(43,31,86,0.12), 0 4px 10px rgba(0,0,0,0.06);
}

.card-title {
  font-weight: 700;
  color: var(--ink);
  margin-bottom: 6px;
}

.kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

@media (max-width: 900px) {
  .kpis {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 420px) {
  .kpis {
    grid-template-columns: 1fr;
  }
}

.kpi {
  background: var(--soft);
  border-radius: 12px;
  padding: 10px 12px;
  text-align: center;
  box-shadow: inset 0 0 0 1px var(--inset);
}

.kpi span {
  display: block;
  font-size: 12px;
  color: var(--muted);
}

.kpi b {
  font-size: clamp(18px, 1.5vw + 12px, 20px);
  color: var(--ink);
  letter-spacing: -0.01em;
}

.sections {
  width: min(980px, 94vw);
  margin: 18px auto 40px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
}

.section {
  background: var(--card-bg);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(43,31,86,0.08), 0 2px 6px rgba(0,0,0,0.04);
  padding: 14px 16px;
  border: 1px solid rgba(45,41,73,0.06);
}

.section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px 12px;
  flex-wrap: wrap;
}

.section-title {
  margin: 0 0 10px;
  color: var(--ink);
}

.legend {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--muted);
  font-size: 13px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  display: inline-block;
}

.dot-completed {
  background: #38D5F4;
}

.trend-wrap {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  align-items: stretch;
  overflow: hidden;
  padding: 2px 0;
}

.trend-canvas {
  width: 100%;
  height: auto;
  display: block;
  touch-action: pan-x pan-y;
  max-width: 100%;
}

.skeleton-chart {
  margin-top: 8px;
  width: 100%;
  height: 16px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f0eefc, #e6e3fb, #f0eefc);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.cats-row {
  --cat-size: 170px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin: 8px 0 14px;
}

.cat-chip {
  display: grid;
  grid-template-rows: auto auto auto;
  grid-template-columns: 1fr;
  justify-items: center;
  align-content: center;
  gap: 4px;
  padding: 12px;
  border-radius: 14px;
  background: var(--soft);
  box-shadow: inset 0 0 0 1px var(--inset);
  color: var(--ink);
  border: none;
  text-align: center;
  cursor: pointer;
  transition: background 140ms ease, transform 140ms ease, box-shadow 140ms ease;
  aspect-ratio: 1 / 1;
  width: var(--cat-size);
  min-width: var(--cat-size);
  max-width: var(--cat-size);
  flex: 0 0 var(--cat-size);
}

.cat-chip:hover {
  transform: translateY(-1px);
  background: #f3f1ff;
}

.cat-chip.active {
  box-shadow: inset 0 0 0 2px var(--accent), 0 6px 18px rgba(56,213,244,0.18);
  background: #eefcff;
}

@media (max-width: 640px) {
  .cats-row {
    --cat-size: min(42vw, 170px);
  }
}

@media (max-width: 380px) {
  .cats-row {
    --cat-size: min(78vw, 170px);
  }
}

.cat-emoji {
  font-size: clamp(22px, 2.2vw + 14px, 28px);
  line-height: 1;
}

.cat-emoji.big {
  font-size: 26px;
}

.cat-title {
  font-weight: 700;
}

.cat-metrics {
  font-size: 12px;
  color: var(--muted);
}

.category-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.category-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 8px 10px;
  background: #faf9ff;
  border-radius: 12px;
  box-shadow: inset 0 0 0 1px #eeeafc;
}

.category-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-name h4 {
  margin: 0;
  font-size: 16px;
  color: var(--ink);
}

.category-totals {
  display: flex;
  gap: 12px;
  color: #444;
  font-size: 14px;
  flex-wrap: wrap;
}

.levels {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;
}

.level-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--soft);
  border-radius: 12px;
  padding: 10px 12px;
  box-shadow: inset 0 0 0 1px var(--inset);
  transition: background 140ms ease, transform 140ms ease;
}

.level-row:hover {
  background: #f3f1ff;
  transform: translateY(-1px);
}

.level-title {
  font-weight: 700;
  color: var(--ink);
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.muted {
  color: var(--muted);
  font-weight: 400;
}

.level-kpis {
  display: flex;
  gap: 12px;
  color: #444;
  font-size: 14px;
  flex-wrap: wrap;
}

@media (max-width: 900px) {
  .category-head {
    flex-direction: column;
    align-items: flex-start;
  }

  .category-totals {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
    text-align: left;
  }

  .category-totals span {
    display: block;
  }

  .level-row {
    display: block;
    align-items: flex-start;
  }

  .level-title {
    width: 100%;
    margin-bottom: 6px;
  }

  .level-kpis {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
    width: 100%;
    margin-top: 0;
    justify-items: start;
    text-align: left;
  }

  .level-kpis span {
    display: block;
  }
}

.error {
  margin-top: 16px;
  color: #b00020;
  background: #fde7eb;
  border: 1px solid #f7c6d1;
  padding: 10px 12px;
  border-radius: 10px;
}
</style>